import { useState } from 'react';
import { clearCachedToken, post, renewToken } from 'src/api/requests';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { CreateEnvironmentDto } from 'src/interfaces/environment.interface';
import { Organization } from 'src/interfaces/organization.interface';
import { ApiKeys } from './components/ApiKeys';
import { Users } from './components/Users';
import { Gear } from '@phosphor-icons/react';
import { CopyableField } from 'src/components/CopyableField';
import { Tooltip } from 'src/components/Tooltip';
import { useOrganizationSwitcher } from 'src/hooks/useOrganizationSwitcher';

export const Settings = () => {
  const { patch, post: authedPost } = useAuthedRequest();
  const notification = useNotification();
  const { hiddenEnvironmentsIds, fetchEnvironments, environmentList } =
    useEnvironment();

  const {
    organizations,
    currentOrg,
    orgsLoading,
    getOrganizationOptions,
    handleChangeOrganization,
    refetchOrganizations,
  } = useOrganizationSwitcher({ returnTo: '/settings' });

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState<CreateEnvironmentDto>({
    name: '',
  });

  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [modalOrganizationOpen, setModalOrganizationOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const isLoading = loading || orgsLoading;

  const handleCreateEnvironment = () => {
    setModalOpen(true);
  };

  const createEnvironment = async () => {
    setLoading(true);

    try {
      await authedPost(`/environments`, newEnvironment);
      await fetchEnvironments();

      notification.success('Environment created successfully');
      setModalOpen(false);
    } catch {
      notification.error('Failed to create environment');
    } finally {
      setLoading(false);
      setNewEnvironment({ name: '' });
    }
  };

  const handleCloseCreateEnvironmentModal = () => {
    setModalOpen(false);
    setNewEnvironment({ name: '' });
  };

  const handleEditOrg = (orgId: string) => {
    const org = organizations.find((org) => org.id === orgId);
    if (!org) return;

    setEditingOrg(org);
    setNewName(org.name);
    setModalOrganizationOpen(true);
  };

  const updateOrganization = async () => {
    if (!editingOrg) return;

    setLoading(true);
    try {
      await patch(`/organizations/${editingOrg.id}`, {
        name: newName,
      });

      if (currentOrg?.value === editingOrg.id) {
        clearCachedToken();

        await renewToken();

        await post(`/users/sync`, {});
      }

      setModalOrganizationOpen(false);

      notification.success('Organization updated successfully');

      await refetchOrganizations();

      clearCachedToken();

      await renewToken();
    } catch {
      notification.error('Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <Heading
        title={
          <>
            <h1>
              Settings
              {currentOrg && <> for</>}
            </h1>

            <AutoCompleteSelectBox
              key={String(currentOrg?.value) + '-' + String(currentOrg?.label)}
              defaultValue={currentOrg}
              fetchOptions={getOrganizationOptions}
              onChange={(value) => handleChangeOrganization(value)}
              className="text-xl font-bold"
              variant="text"
              size="medium"
              disabled={isLoading}
              defaultOptions={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
              allowRemoveOption={false}
            />

            <Tooltip content="Change Organization details" position="bottom">
              <Button
                variant="text"
                size="small"
                onClick={() => handleEditOrg(currentOrg?.value || '')}
              >
                <Gear className="w-6 h-6 text-primary" />
              </Button>
            </Tooltip>
          </>
        }
        subtitle={
          <div className="flex flex-row items-center gap-x-2">
            {currentOrg && (
              <>
                <strong>ID:</strong>{' '}
                <CopyableField
                  value={currentOrg.value}
                  notification={notification}
                />
              </>
            )}
          </div>
        }
      ></Heading>

      {isLoading && <Loading className="p-4" />}

      {!isLoading && (
        <ContentWrapper>
          <Can operation="read" subject={Subject.ENVIRONMENTS}>
            <ApiKeys
              hiddenEnvironmentsIds={hiddenEnvironmentsIds}
              fetchEnvironments={fetchEnvironments}
              environmentList={environmentList}
              handleCreateEnvironment={handleCreateEnvironment}
            />
          </Can>

          <Can operation="read" subject={Subject.USERS}>
            <Users />
          </Can>
        </ContentWrapper>
      )}

      <Modal
        title={`Create Environment`}
        isOpen={modalOpen}
        onClose={() => handleCloseCreateEnvironmentModal()}
        actionButton={
          <Button
            onClick={createEnvironment}
            disabled={loading || !newEnvironment || !newEnvironment.name}
          >
            Create Environment
          </Button>
        }
      >
        <div className="flex justify-end italic text-sm">* Required fields</div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <Input
              label="Name"
              type="text"
              value={newEnvironment.name}
              onChange={(e) =>
                setNewEnvironment({ ...newEnvironment, name: e.target.value })
              }
              required
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Edit Organization"
        isOpen={modalOrganizationOpen}
        onClose={() => setModalOrganizationOpen(false)}
        actionButton={
          <Button
            onClick={updateOrganization}
            disabled={loading || !newName || newName === editingOrg?.name}
          >
            Update Organization
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-6">
          <div>
            <Input
              label="Organization Name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
